import React, { useState, useEffect } from "react";
import {
  Spin, Input, Select, DatePicker, InputNumber,
  Modal, Form, message, Popconfirm, Tag,
} from "antd";
import {
  LockOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api, { extractFireDACData } from "../services/api";

const { Option } = Select;
const DARK_NAV = "#000000";
const MOBILE_BREAKPOINT = 768;

const COMPANHIAS_BLOQUEIO = [
  "EMIRATES",
  "SOUTH AFRICAN",
  "AMERICAN AIRLINES",
  "KLM",
  "AIR FRANCE",
  "TURKISH AIRLINES",
];

// ── Responsividade (mesmo padrão dos outros módulos) ───────────────────────
function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    handler();
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

const normalize = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};

const isDataVazia = (val) => {
  if (val === null || val === undefined) return true;
  const s = String(val).trim();
  if (s === "" || s === "null" || s === "undefined" || s === "0") return true;
  if (s.includes("1899-12-30") || s.includes("18991230")) return true;
  return false;
};

const formatData = (val, vazio = "—") => {
  if (isDataVazia(val)) return vazio;
  const s = String(val);
  let m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  m = s.match(/(\d{4})(\d{2})(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toLocaleDateString("pt-BR");
  return s;
};

const dataSortKey = (val) => {
  if (isDataVazia(val)) return "99999999"; // sem data vai pro fim, não pro topo
  const s = String(val);
  let m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}${m[2]}${m[3]}`;
  m = s.match(/(\d{4})(\d{2})(\d{2})/);
  if (m) return `${m[1]}${m[2]}${m[3]}`;
  return "99999999";
};

// Converte a data crua do FireDAC (ISO ou YYYYMMDD) num Date real, pra dar
// pra comparar com "hoje" e "hoje + 3 meses".
const parseDataParaDate = (val) => {
  if (isDataVazia(val)) return null;
  const s = String(val);
  let m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = s.match(/(\d{4})(\d{2})(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const fmtMilhas = (v) => {
  if (v === "" || v === undefined || v === null) return "";
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};
const parseMilhas = (v) => {
  if (!v) return 0;
  const n = parseInt(String(v).replace(/\./g, ""), 10);
  return isNaN(n) ? 0 : n;
};

const STATUS_COR = {
  BLOQUEADO: { bg: "#fff7e6", cor: "#d48806", borda: "#ffe7ba" },
  PAGO:      { bg: "#f6ffed", cor: "#389e0d", borda: "#b7eb8f" },
  CANCELADO: { bg: "#fff1f0", cor: "#cf1322", borda: "#ffa39e" },
};

export default function Bloqueio({ userId, isCliente }) {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState("");
  const [filtroCliente, setFiltroCliente] = useState(null);
  const [filtroStatus, setFiltroStatus]   = useState("TODOS");

  const [bloqueios, setBloqueios] = useState([]);
  const [clientes, setClientes]   = useState([]);

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando]       = useState(null); // registro sendo editado
  const [form] = Form.useForm();

  const showCliente = !isCliente;

  useEffect(() => {
    if (!userId) return;
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isCliente]);

  const carregar = async () => {
    setLoading(true);
    try {
      let alvos = [];
      if (isCliente) {
        const nomeCliente = localStorage.getItem("userName") || "Você";
        alvos = [{ id: userId, nome: nomeCliente }];
      } else {
        const r = await api.get("/ServerPrincipal/PesquisaClientes", { params: { Login: userId } });
        alvos = extractFireDACData(r.data).map((c) => ({
          id:   c.cli_id || c.id,
          nome: c.cli_nome || c.nome || "Cliente",
        }));
      }
      setClientes(alvos);

      const bloqueiosAll = [];
      await Promise.all(
        alvos.map(async (alvo) => {
          try {
            const r = await api.get("/ServerPrincipal/PesquisaBloqueio", { params: { Cliente: alvo.id } });
            extractFireDACData(r.data).map(normalize).forEach((row) => {
              bloqueiosAll.push({
                id:                  row.blo_id,
                clienteId:           alvo.id,
                clienteNome:         row.cli_nome || alvo.nome,
                cartaoId:            row.car_id,
                programa:            row.car_nome_programa || "",
                cartaoQtdeBloqueada: Number(row.car_qtde_bloqueada || 0),
                companhia:           row.blo_companhia_aerea || "",
                qtdeBloqueio:        Number(row.blo_qtde_milhas || 0),
                qtdeFalta:           Number(row.blo_qtde_milhas_falta || 0),
                origem:              row.blo_origem || "",
                destino:             row.blo_destino || "",
                dataLimiteRaw:       row.blo_data_limite_pagamento || "",
                status:              (row.blo_status || "BLOQUEADO").toUpperCase(),
                dataBloqueio:        row.blo_data_bloqueio || "",
                observacoes:         row.blo_observacao || "",
              });
            });
          } catch (e) {
            console.warn(`[Bloqueio] cliente ${alvo.id}:`, e);
          }
        })
      );

      // Mais urgente (prazo mais próximo) primeiro.
      bloqueiosAll.sort((a, b) => dataSortKey(a.dataLimiteRaw).localeCompare(dataSortKey(b.dataLimiteRaw)));
      setBloqueios(bloqueiosAll);
    } catch (e) {
      console.error(e);
      message.error("Erro ao carregar bloqueios.");
    } finally {
      setLoading(false);
    }
  };

  // ── Urgência: vence em até 3 meses, ainda bloqueado e ainda falta milha ──
  const hoje = new Date();
  const limite3Meses = new Date();
  limite3Meses.setMonth(limite3Meses.getMonth() + 3);

  const comUrgencia = bloqueios.map((b) => {
    const dataLimite = parseDataParaDate(b.dataLimiteRaw);
    const vencido = dataLimite ? dataLimite < hoje : false;
    const urgente =
      !vencido &&
      dataLimite &&
      dataLimite <= limite3Meses &&
      b.status === "BLOQUEADO" &&
      b.qtdeFalta > 0;
    return { ...b, dataLimite, vencido, urgente };
  });

  const qtdeUrgentes = comUrgencia.filter((b) => b.urgente).length;
  const qtdeVencidos = comUrgencia.filter((b) => b.vencido && b.status === "BLOQUEADO").length;

  // ── Filtros de busca ────────────────────────────────────────────────────
  const dados = comUrgencia.filter((b) => {
    if (filtroCliente && b.clienteId !== filtroCliente) return false;
    if (filtroStatus !== "TODOS" && b.status !== filtroStatus) return false;
    if (!search) return true;
    const t = search.toLowerCase();
    return [b.companhia, b.origem, b.destino, b.clienteNome]
      .some((c) => String(c || "").toLowerCase().includes(t));
  });

  // ── Editar ──────────────────────────────────────────────────────────────
  const abrirEdicao = (registro) => {
    setEditando(registro);
    form.setFieldsValue({
      companhiaAerea: registro.companhia,
      origem: registro.origem,
      destino: registro.destino,
      qtdeBloqueio: registro.qtdeBloqueio,
      dataLimitePagamento: registro.dataLimiteRaw ? dayjs(registro.dataLimiteRaw) : null,
      status: registro.status,
      observacoes: registro.observacoes,
    });
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setEditando(null);
    form.resetFields();
  };

  const confirmarSalvar = (values) => {
    const statusMudouParaPago = values.status === "PAGO" && editando.status !== "PAGO";
    if (statusMudouParaPago) {
      Modal.confirm({
        title: "Confirmar pagamento do bloqueio?",
        content: `Isso vai debitar ${values.qtdeBloqueio.toLocaleString("pt-BR")} milhas do saldo Smiles agora. Essa ação gera uma transação de saída.`,
        okText: "Sim, confirmar pagamento",
        cancelText: "Cancelar",
        onOk: () => salvarEdicao(values),
      });
    } else {
      salvarEdicao(values);
    }
  };

  const salvarEdicao = async (values) => {
    setSaving(true);
    try {
      const payload = {
        IDBloqueio:          editando.id,
        CompanhiaAerea:      values.companhiaAerea,
        QtdeBloqueio:        String(values.qtdeBloqueio),
        Origem:              values.origem || "",
        Destino:             values.destino || "",
        DataLimitePagamento: values.dataLimitePagamento
          ? values.dataLimitePagamento.format("DD/MM/YYYY HH:mm:ss")
          : "",
        Status:              values.status,
        Observacoes:          values.observacoes || "",
      };

      await api.post("/ServerPrincipal/AtualizarBloqueio", payload);
      message.success("Bloqueio atualizado com sucesso!");
      fecharModal();
      carregar();
    } catch (e) {
      const errMsg = e?.response?.data || e?.message || "Erro ao atualizar bloqueio";
      message.error(String(errMsg));
    } finally {
      setSaving(false);
    }
  };

  // ── Excluir ─────────────────────────────────────────────────────────────
  const excluir = async (registro) => {
    try {
      await api.post("/ServerPrincipal/DeletarBloqueio", { IDBloqueio: registro.id });
      message.success("Bloqueio excluído.");
      carregar();
    } catch (e) {
      const errMsg = e?.response?.data || e?.message || "Erro ao excluir bloqueio";
      message.error(String(errMsg));
    }
  };

  // ── Styles ──────────────────────────────────────────────────────────────
  const s = {
    lbl: { fontSize: 12, color: "#555", fontWeight: 500, marginBottom: 4, display: "block" },
  };

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
        <LockOutlined style={{ fontSize: 18, color: "#555" }} />
        <span style={{ fontSize: 18, fontWeight: 600, color: "#222" }}>Bloqueios de Assento</span>
      </div>

      {/* Alerta de urgência */}
      {(qtdeUrgentes > 0 || qtdeVencidos > 0) && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          background: qtdeVencidos > 0 ? "#fff1f0" : "#fff7e6",
          border: `1px solid ${qtdeVencidos > 0 ? "#ffa39e" : "#ffe7ba"}`,
          borderRadius: 8, padding: "12px 16px", marginBottom: 16,
        }}>
          <WarningOutlined style={{ fontSize: 18, color: qtdeVencidos > 0 ? "#cf1322" : "#d48806" }} />
          <div style={{ fontSize: 13, color: "#333" }}>
            {qtdeVencidos > 0 && (
              <div><strong>{qtdeVencidos}</strong> bloqueio(s) com prazo de pagamento já vencido e ainda em aberto.</div>
            )}
            {qtdeUrgentes > 0 && (
              <div><strong>{qtdeUrgentes}</strong> bloqueio(s) vencem em até 3 meses e ainda faltam milhas — providencie as milhas que faltam antes do prazo.</div>
            )}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{
        background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8",
        padding: isMobile ? "12px 14px" : "14px 18px", marginBottom: 16,
      }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: 1, minWidth: isMobile ? "100%" : 220 }}>
            <label style={s.lbl}>Buscar por companhia, trecho ou cliente</label>
            <Input
              placeholder="Digite para filtrar..."
              prefix={<SearchOutlined style={{ color: "#bbb" }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </div>

          <div style={{ minWidth: isMobile ? "100%" : 180 }}>
            <label style={s.lbl}>Status</label>
            <Select style={{ width: "100%" }} value={filtroStatus} onChange={setFiltroStatus}>
              <Option value="TODOS">Todos</Option>
              <Option value="BLOQUEADO">Bloqueado</Option>
              <Option value="PAGO">Pago</Option>
              <Option value="CANCELADO">Cancelado</Option>
            </Select>
          </div>

          {showCliente && (
            <div style={{ minWidth: isMobile ? "100%" : 200 }}>
              <label style={s.lbl}>Cliente</label>
              <Select
                placeholder="Todos os clientes"
                allowClear
                style={{ width: "100%" }}
                value={filtroCliente}
                onChange={setFiltroCliente}
                showSearch
                optionFilterProp="children"
              >
                {clientes.map((c) => (
                  <Option key={c.id} value={c.id}>{c.nome}</Option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Lista */}
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8" }}>
        <Spin spinning={loading}>
          {dados.length === 0 && !loading ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#bbb", fontSize: 14 }}>
              Nenhum bloqueio encontrado
            </div>
          ) : (
            dados.map((b) => {
              const statusCor = STATUS_COR[b.status] || STATUS_COR.BLOQUEADO;
              return (
                <div
                  key={b.id}
                  style={{
                    padding: "14px 18px",
                    borderBottom: "1px solid #f5f5f5",
                    borderLeft: b.vencido ? "4px solid #cf1322" : b.urgente ? "4px solid #d48806" : "4px solid transparent",
                    display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
                  }}
                >
                  <div style={{ flex: "1 1 220px" }}>
                    {showCliente && (
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#222" }}>{b.clienteNome}</div>
                    )}
                    <div style={{ fontSize: 13, color: "#555" }}>
                      {b.companhia} · {b.origem || "—"} → {b.destino || "—"}
                    </div>
                    <div style={{ fontSize: 11, color: "#999" }}>
                      {b.programa}
                      {b.cartaoQtdeBloqueada > 0 && (
                        <> · comprometido no cartão: {b.cartaoQtdeBloqueada.toLocaleString("pt-BR")}</>
                      )}
                    </div>
                  </div>

                  <div style={{ flex: "0 0 120px", fontSize: 13 }}>
                    <div style={{ color: "#888", fontSize: 11 }}>Milhas Bloqueio</div>
                    <div style={{ fontWeight: 600 }}>{b.qtdeBloqueio.toLocaleString("pt-BR")}</div>
                  </div>

                  <div style={{ flex: "0 0 120px", fontSize: 13 }}>
                    <div style={{ color: "#888", fontSize: 11 }}>Milhas em Falta</div>
                    <div style={{ fontWeight: 600, color: b.qtdeFalta > 0 ? "#d48806" : "#389e0d" }}>
                      {b.qtdeFalta > 0 ? b.qtdeFalta.toLocaleString("pt-BR") : "Completo"}
                    </div>
                  </div>

                  <div style={{ flex: "0 0 120px", fontSize: 13 }}>
                    <div style={{ color: "#888", fontSize: 11 }}>Prazo Pagamento</div>
                    <div style={{ fontWeight: 600, color: b.vencido ? "#cf1322" : b.urgente ? "#d48806" : "#333" }}>
                      {formatData(b.dataLimiteRaw)}
                    </div>
                  </div>

                  <div style={{ flex: "0 0 110px" }}>
                    <Tag style={{
                      background: statusCor.bg, color: statusCor.cor, border: `1px solid ${statusCor.borda}`,
                      fontWeight: 600, fontSize: 11,
                    }}>
                      {b.status}
                    </Tag>
                    {b.vencido && b.status === "BLOQUEADO" && (
                      <div style={{ fontSize: 10, color: "#cf1322", marginTop: 2 }}>VENCIDO</div>
                    )}
                    {!b.vencido && b.urgente && (
                      <div style={{ fontSize: 10, color: "#d48806", marginTop: 2 }}>PRAZO PRÓXIMO</div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => abrirEdicao(b)}
                      style={{
                        border: "1px solid #d9d9d9", background: "#fafafa", borderRadius: 4,
                        padding: "6px 10px", cursor: "pointer", color: "#555",
                      }}
                      title="Editar"
                    >
                      <EditOutlined />
                    </button>
                    <Popconfirm
                      title="Excluir este bloqueio?"
                      description={b.status === "PAGO" ? "Como já está pago, o saldo será estornado ao cartão." : "Essa ação não pode ser desfeita."}
                      okText="Excluir"
                      cancelText="Cancelar"
                      onConfirm={() => excluir(b)}
                    >
                      <button
                        style={{
                          border: "1px solid #ffccc7", background: "#fff1f0", borderRadius: 4,
                          padding: "6px 10px", cursor: "pointer", color: "#cf1322",
                        }}
                        title="Excluir"
                      >
                        <DeleteOutlined />
                      </button>
                    </Popconfirm>
                  </div>
                </div>
              );
            })
          )}
        </Spin>
      </div>

      {/* Modal de edição */}
      <Modal
        title="Editar Bloqueio"
        open={modalAberto}
        onCancel={fecharModal}
        onOk={() => form.submit()}
        okText={saving ? "Salvando..." : "Salvar"}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={confirmarSalvar}>
          <Form.Item name="companhiaAerea" label="Companhia Aérea" rules={[{ required: true }]}>
            <Select>
              {COMPANHIAS_BLOQUEIO.map((c) => <Option key={c} value={c}>{c}</Option>)}
            </Select>
          </Form.Item>

          <div style={{ display: "flex", gap: 10 }}>
            <Form.Item name="origem" label="Origem" style={{ flex: 1 }} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="destino" label="Destino" style={{ flex: 1 }} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <Form.Item name="qtdeBloqueio" label="Milhas do Bloqueio" style={{ flex: 1 }} rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} min={0} controls={false}
                formatter={fmtMilhas} parser={parseMilhas}
              />
            </Form.Item>
            <Form.Item name="dataLimitePagamento" label="Data Limite de Pagamento" style={{ flex: 1 }} rules={[{ required: true }]}>
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </div>

          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select>
              <Option value="BLOQUEADO">Bloqueado</Option>
              <Option value="PAGO">Pago (debita o saldo Smiles agora)</Option>
              <Option value="CANCELADO">Cancelado</Option>
            </Select>
          </Form.Item>

          <Form.Item name="observacoes" label="Observações">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}